import { useEffect, useState } from 'react';
import { AudioPlayerBar } from '@/components/review/AudioPlayerBar';
import type { MediaPlayerControls, MediaPlayerState } from '@/hooks/useMediaPlayer';

interface MediaPlaybackPanelProps {
  state: MediaPlayerState;
  controls: MediaPlayerControls;
  loadError?: string | null;
  className?: string;
}

export function MediaPlaybackPanel({
  state,
  controls,
  loadError,
  className = '',
}: MediaPlaybackPanelProps) {
  const [hasVideoPreview, setHasVideoPreview] = useState(false);
  const showVideoStartOverlay = state.kind === 'video' && !state.isPlaying && state.currentTime < 0.5;
  const showVideoElement = !showVideoStartOverlay || hasVideoPreview;
  const showBufferingOverlay = state.kind === 'video' && state.isBuffering && !showVideoStartOverlay;
  const loadedText = state.kind === 'video' ? '動画ロード済み' : '音声ロード済み';
  const statusText = state.loadError || loadError || (state.isBuffering ? '読込中...' : state.isLoaded ? loadedText : '読込中...');

  useEffect(() => {
    setHasVideoPreview(false);
  }, [state.sourceUrl]);

  const handleVideoLoadedMetadata = (event: React.SyntheticEvent<HTMLVideoElement>) => {
    const video = event.currentTarget;
    if (video.readyState >= 2) {
      setHasVideoPreview(true);
      return;
    }
    try {
      video.currentTime = Math.min(0.1, video.duration || 0.1);
    } catch {
      // Some browsers/files do not allow seeking before enough data is available.
    }
  };

  return (
    <div className={`mt-4 flex flex-col rounded-lg border border-gray-200 bg-white ${className}`}>
      <div className="flex items-center justify-between border-b border-gray-100 px-3 py-2">
        <span className="text-xs text-gray-500">メディア再生</span>
        <span className={`text-xs ${state.isLoaded ? 'text-gray-500' : 'text-gray-400'}`}>
          {statusText}
        </span>
      </div>

      {state.kind === 'video' && state.sourceUrl && (
        <div className="relative aspect-video max-h-[28rem] w-full overflow-hidden bg-black">
          <video
            ref={controls.bindMediaElement}
            src={state.sourceUrl}
            controls={false}
            preload="metadata"
            muted={false}
            playsInline
            onLoadedMetadata={handleVideoLoadedMetadata}
            onLoadedData={() => setHasVideoPreview(true)}
            className={`h-full w-full object-contain transition-opacity ${
              showVideoElement ? 'opacity-100' : 'opacity-0'
            }`}
          />
          {showVideoStartOverlay && (
            <button
              type="button"
              onClick={controls.togglePlayPause}
              disabled={!state.isLoaded}
              className="absolute inset-0 flex items-center justify-center text-white disabled:cursor-wait disabled:opacity-60"
              aria-label="動画を再生"
            >
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-white/90 text-gray-900 shadow-lg transition-transform hover:scale-105">
                <svg className="ml-1 h-7 w-7" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                  <path d="M6.5 5.5v9l7-4.5-7-4.5z" />
                </svg>
              </span>
            </button>
          )}
          {showBufferingOverlay && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/20" aria-label="読込中">
              <div className="h-9 w-9 animate-spin rounded-full border-2 border-white/40 border-t-white" />
            </div>
          )}
        </div>
      )}

      {state.kind === 'audio' && state.sourceUrl && (
        <audio
          ref={controls.bindMediaElement}
          src={state.sourceUrl}
          preload="auto"
          aria-hidden="true"
          tabIndex={-1}
          className="absolute h-px w-px opacity-0"
        />
      )}

      {state.isLoaded && <AudioPlayerBar state={state} controls={controls} />}
    </div>
  );
}
